import React, {type ReactElement} from 'react';

import {Location} from 'history';
import {isString} from 'lodash';

import {GenericDiffer} from 'app/components/general/GenericDiffer';
import {getHtmlDiff, getListsDiff} from 'app/utils/showdiff';

import type {LocationQuery} from 'app/components/general/VersionsSidebar/location-query';
import type {ProcedureVersionWithFormattedType} from 'app/procedures/types';
import type {ProcessVersionWithFormattedType} from 'app/process/types';
import type {
  AnyStepType,
  AttachedChoiceType,
  AttachedEmbed,
  AttachedFile,
  AttachedMedia,
  ChoicesStepFieldType,
  DocumentLanesRegistryType,
  ProcedureParsedContent,
  StepFieldType,
} from 'sweet-types';

type LocationQueryType = LocationQuery & {hidediff?: string};

type DocumentContentType = {
  url: string;
  name: string;
  html_url: string;
  is_large?: boolean;
  content: Array<AnyStepType>;
  lanes: DocumentLanesRegistryType;
  description: ProcedureParsedContent['description'];
};

export type VersionDiffType = {
  html_url: string;
  name: string | JSX.Element;
  is_large?: boolean;
  description: Omit<ProcedureParsedContent['description'], 'content'> & {
    content: string | JSX.Element;
  };
  lanesDiff: Record<string, {new: string | null; old: string | null}> | null;
} & Pick<DocumentContentType, 'url' | 'lanes' | 'content'>;

type Props = {
  versionAvailable: boolean;
  previousVersion:
    | ProcedureVersionWithFormattedType
    | ProcessVersionWithFormattedType
    | null;
  location: Location<LocationQueryType>;
  content: DocumentContentType;
  children: (
    content: VersionDiffType,
    showingDiff: boolean,
    toggleDiff: () => void
  ) => ReactElement;
};

export const VersionDiffer = ({
  location,
  versionAvailable,
  previousVersion,
  content,
  children,
}: Props) => {
  const getBlankVersion = () => ({
    name: '',
    lanes: {},
    content: [],
    description: {
      content: '',
      images: [],
      files: [],
      embeds: [],
    },
  });

  function getBlankStep<T extends AnyStepType>(step: T): T {
    const blank: T = {...step};

    if (blank.type === 'content') {
      blank.title = '';
      blank.content = '';
      blank.next = '';
      blank.images = [];
      blank.embeds = [];
      blank.fields = [];
    } else if (blank.type === 'decision') {
      blank.title = '';
      blank.content = '';
      blank.choices = [];
    } else if (blank.type === 'ending') {
      blank.title = '';
      blank.content = '';
    } else if (blank.type === 'processprocedure' && blank.deleted !== true) {
      blank.title = '';
      blank.next = '';
      blank.procedure_id = 0;
      blank.hashid = '';
      blank.name = '';
      blank.current_version = {
        id: 0,
        url: '',
        html_url: '',
        content_type: 'procedureversion',
      };
    } else if (blank.type === 'processprocedure' && blank.deleted === true) {
      blank.next = '';
    }
    return blank;
  }

  const getStepDiff = (
    oldStep: AnyStepType,
    step: AnyStepType,
    changedState?: 'ins' | 'del'
  ) => ({
    ...step,
    changedState,
    title: getHtmlDiff(oldStep.title || '', step.title || ''),
    content: getHtmlDiff(
      oldStep.type !== 'processprocedure' ? oldStep.content : '',
      step.type !== 'processprocedure' ? step.content : '',
      false
    ),
    choices: getChoiceDiff(
      oldStep.type === 'decision' ? oldStep.choices : [],
      step.type === 'decision' ? step.choices : []
    ),
    fields: getFieldDiff(
      oldStep.type === 'content' ? oldStep.fields || [] : [],
      step.type === 'content' ? step.fields || [] : []
    ),
    images: getImagesDiff(
      oldStep.type === 'content' ? oldStep.images : [],
      step.type === 'content' ? step.images : []
    ),
    files: getFileDiff(
      oldStep.type === 'content' ? oldStep.files : [],
      step.type === 'content' ? step.files : []
    ),
    embeds: getEmbedsDiff(
      oldStep.type === 'content' ? oldStep.embeds : [],
      step.type === 'content' ? step.embeds : []
    ),
    movedFrom: oldStep.n !== step.n ? oldStep.n : undefined,
    next: getNextDiff(
      oldStep.type === 'content' || oldStep.type === 'processprocedure'
        ? oldStep.next || ''
        : '',
      step.type === 'content' || step.type === 'processprocedure'
        ? step.next || ''
        : ''
    ),
  });

  const getFileDiff = (
    oldList: Array<AttachedFile>,
    newList: Array<AttachedFile>
  ) => {
    return getListsDiff(
      oldList,
      newList,
      (file) => file.file_ref,
      (file) => ({...file, changedState: 'ins'}),
      undefined, // We don't really care about updated state because one always del and ins a file
      (file) => ({...file, changedState: 'del'})
    );
  };

  const getEmbedsDiff = (
    oldList: Array<AttachedEmbed>,
    newList: Array<AttachedEmbed>
  ) => {
    return getListsDiff(
      oldList,
      newList,
      (file) => file.embed_html,
      (file) => ({...file, changedState: 'ins'}),
      undefined, // We don't really care about updated state because one always del and ins a file
      (file) => ({...file, changedState: 'del'})
    );
  };

  const getImagesDiff = (
    oldImages: Array<AttachedMedia>,
    images: Array<AttachedMedia>
  ) => {
    return getListsDiff(
      oldImages,
      images,
      (image) =>
        image.image_ref != null ? image.image_ref : image.embed_thumb,
      (image) => ({...image, changedState: 'ins'}),
      undefined, // We don't really care about updated state because one always del and ins an embed video/image
      (image) => ({...image, changedState: 'del'})
    );
  };

  const getFieldDiff = (
    oldFields: Array<StepFieldType>,
    newFields: Array<StepFieldType>
  ) =>
    getListsDiff(
      oldFields,
      newFields,
      (field) => field.id,
      (field) => ({...field, changedState: 'ins'}),
      (oldField, newField) =>
        (newField.type === 'checkbox' || newField.type === 'radio') &&
        (oldField.type === 'checkbox' || oldField.type === 'radio')
          ? {
              ...newField,
              label: getHtmlDiff(oldField.label, newField.label, false),
              choices: getFieldChoicesDiff(oldField.choices, newField.choices),
            }
          : {
              ...newField,
              label: getHtmlDiff(oldField.label, newField.label, false),
            },
      (field) => ({...field, changedState: 'del'})
    );

  const getFieldChoicesDiff = (
    oldChoices: ChoicesStepFieldType['choices'],
    newChoices: ChoicesStepFieldType['choices']
  ) =>
    getListsDiff(
      oldChoices,
      newChoices,
      (choice) => choice.id,
      (choices) => ({...choices, changedState: 'ins'}),
      (oldChoices, newChoices) => ({
        ...newChoices,
        label: getHtmlDiff(oldChoices.label, newChoices.label),
      }),
      (choices) => ({...choices, changedState: 'del'})
    );

  const getChoiceDiff = (
    oldChoices: Array<AttachedChoiceType>,
    newChoices: Array<AttachedChoiceType>
  ) =>
    getListsDiff(
      oldChoices,
      newChoices,
      (choice) => choice.id,
      (choices) => ({...choices, changedState: 'ins'}),
      (oldChoices, newChoices) => ({
        ...newChoices,
        name: getHtmlDiff(oldChoices.name, newChoices.name),
      }),
      (choices) => ({...choices, changedState: 'del'})
    );

  const getNextDiff = (oldNext: string, newNext: string) =>
    oldNext === newNext ? newNext : {old: oldNext, new: newNext};

  const getContentDiff = (
    oldContent: Array<AnyStepType>,
    newContent: Array<AnyStepType>
  ) =>
    getListsDiff(
      oldContent,
      newContent,
      (step) => (step.id != null ? step.id : step.n),
      (step) => getStepDiff(getBlankStep(step), step, 'ins'),
      (oldStep, step) => getStepDiff(oldStep, step),
      (step) => getStepDiff(step, getBlankStep(step), 'del')
    );

  const getDescriptionDiff = (
    oldDesc: ProcedureParsedContent['description'],
    newDesc: ProcedureParsedContent['description']
  ) => ({
    files: getFileDiff(oldDesc.files || [], newDesc.files || []),
    images: getImagesDiff(oldDesc.images || [], newDesc.images || []),
    embeds: getEmbedsDiff(oldDesc.embeds || [], newDesc.embeds || []),
    content: getHtmlDiff(oldDesc.content || '', newDesc.content || '', false),
  });

  const getLanesDiff = (
    oldLanes: DocumentLanesRegistryType,
    newLanes: DocumentLanesRegistryType
  ): Record<string, {new: string | null; old: string | null}> | null => {
    const allSteps = new Set([
      ...Object.keys(oldLanes),
      ...Object.keys(newLanes),
    ]);

    let lanesDiff = {};

    for (const stepId of Array.from(allSteps)) {
      const oldLane = oldLanes[stepId];
      const newLane = newLanes[stepId];

      if (
        (oldLane != null && newLane != null && oldLane !== newLane) ||
        (oldLane == null && newLane != null) ||
        (oldLane != null && newLane == null)
      ) {
        lanesDiff[stepId] = {
          old: oldLane == null ? null : oldLane,
          new: newLane == null ? null : newLane,
        };
      }
    }
    return Object.keys(lanesDiff).length > 0 ? lanesDiff : null;
  };

  const getDiffContentFromVersions = (content: DocumentContentType) => {
    const prevVersion = previousVersion ?? getBlankVersion();

    return {
      ...content,
      name: getHtmlDiff(prevVersion.name, content.name),
      description: getDescriptionDiff(
        prevVersion.description,
        content.description
      ),
      content: getContentDiff(prevVersion.content, content.content),
      lanesDiff: getLanesDiff(prevVersion.lanes, content.lanes),
    };
  };

  return (
    <GenericDiffer
      content={content}
      location={location}
      versionAvailable={versionAvailable}
      getDiffContent={() => getDiffContentFromVersions(content)}
    >
      {children}
    </GenericDiffer>
  );
};

// Function to extract content from description whether description is of type object or string
export const getDescriptionContent = (
  description:
    | string
    | (Omit<ProcedureParsedContent['description'], 'content'> & {
        content: string | JSX.Element;
      })
) => (isString(description) ? description : description.content);
